<template>
  <VueFinalModal
    :model-value="show"
    @update:model-value="$emit('update:show', $event)"
    :click-to-close="true"
    :esc-to-close="true"
    classes="documents-modal"
    content-class="confirm-modal-content modal--history"
  >
    <template #default>
      <button
        class="modal__close icons-button"
        @click="$emit('update:show', false)"
        title="Chiudi"
        type="button"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>

      <h3 class="modal__title">Storico movimenti — {{ itemName }}</h3>

      <!-- Loading -->
      <div v-if="isLoading" class="history-loading">
        <div class="spinner" role="status"></div>
        <p>Carico storico…</p>
      </div>

      <!-- Errore -->
      <p v-else-if="hasError" class="history-empty">
        Errore nel caricamento dello storico.
      </p>

      <!-- Vuoto -->
      <p v-else-if="movements.length === 0" class="history-empty">
        Nessun movimento registrato per questo articolo.
      </p>

      <!-- Tabella -->
      <div v-else class="tbl-wrap">
        <table class="tbl history-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Quantità</th>
              <th>Cliente</th>
              <th>Preventivo</th>
              <th>Operatore</th>
              <th>Data</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="mov in movements" :key="mov.id">
              <td>
                <span :class="['status-badge', mov.type]">
                  {{
                    mov.type === "ordine"
                      ? "Ordine"
                      : mov.type === "consegna"
                      ? "Consegna"
                      : mov.type === "ritiro"
                      ? "Ritiro"
                      : mov.type === "reso"
                      ? "Reso"
                      : "Vendita"
                  }}
                </span>
              </td>
              <td :class="mov.quantity < 0 ? 'qty-negative' : 'qty-positive'">
                {{ mov.quantity > 0 ? "+" + mov.quantity : mov.quantity }}
                <span
                  v-if="mov.damagedQty > 0"
                  class="damaged-badge"
                  :title="`${mov.damagedQty} ${
                    isBoxUnit ? 'scatole' : 'pezzi'
                  } danneggiati`"
                >
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  {{ mov.damagedQty }} {{ isBoxUnit ? "sc." : "pz" }} dann.
                </span>
              </td>
              <td>{{ mov.clientName || "—" }}</td>
              <td>
                <router-link
                  v-if="mov.quoteId"
                  :to="`/home/quotes/edit-quote/${mov.quoteId}`"
                  @click="$emit('update:show', false)"
                  >#{{ mov.quoteId }}</router-link
                >
                <span v-else>—</span>
              </td>
              <td>{{ mov.operator || "—" }}</td>
              <td>{{ formatDate(mov.date) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginazione -->
      <div
        v-if="pagination.totalItems > 0"
        class="pagination history-pagination"
      >
        <div class="pagination-left"></div>
        <div class="pagination-right">
          <div class="pagination-info">
            {{ pagination.startItem }}–{{ pagination.endItem }} di
            {{ pagination.totalItems }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="pagination.currentPage === 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="pagination.currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="pagination.currentPage === pagination.totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="pagination.currentPage === pagination.totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <button class="secondary-btn" @click="$emit('update:show', false)">
          Chiudi
        </button>
      </div>
    </template>
  </VueFinalModal>
</template>

<script>
import { VueFinalModal } from "vue-final-modal";
import axiosInstance from "@/axiosInstance";

export default {
  name: "ItemHistoryModal",
  components: { VueFinalModal },
  props: {
    itemId: {
      type: [String, Number],
      required: true,
    },
    itemName: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
    category: {
      type: String,
      default: "",
    },
  },
  emits: ["update:show"],
  computed: {
    isBoxUnit() {
      return [
        "piastrelle",
        "rivestimenti parete",
        "parquet",
        "laminati",
        "decking esterno",
        "profili",
        "sistemi di scarico",
      ].includes(this.category);
    },
    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  data() {
    return {
      movements: [],
      isLoading: false,
      hasError: false,
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        startItem: 1,
        endItem: 0,
      },
    };
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) this.loadHistory();
      },
    },
  },
  methods: {
    async loadHistory(page = 1) {
      this.isLoading = true;
      this.hasError = false;
      this.movements = [];
      try {
        const { data } = await axiosInstance.get(
          `/item/${this.itemId}/history`,
          {
            params: { page, limit: 10 },
          }
        );
        this.movements = data.movements;
        const p = data.pagination;
        this.pagination = {
          currentPage: p.currentPage,
          totalPages: p.totalPages,
          totalItems: p.totalItems,
          itemsPerPage: p.itemsPerPage,
          startItem: (p.currentPage - 1) * p.itemsPerPage + 1,
          endItem: Math.min(p.currentPage * p.itemsPerPage, p.totalItems),
        };
      } catch {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.loadHistory(page);
    },
    formatDate(iso) {
      if (!iso) return "—";
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
.history-pagination {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}
.damaged-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #dc2626;
  font: 600 11px "Inter", sans-serif;
  white-space: nowrap;
  cursor: default;
}
</style>
